import { useEffect } from "react";
import { api, getAccessToken, useAuth } from "../auth/AuthContext";
import { useTheme } from "./ThemeProvider";

/**
 * Binds the theme to the signed-in person (KEY-755). Renders nothing; sits inside the auth provider because
 * the theme provider sits outside it (the sign-in page is themed too).
 *
 * Re-binds whenever the session's person changes — a sign-in, a sign-out, and a PIN unlock on a shared
 * tablet, where the theme should become the unlocking person's (the server admits `/api/me/theme` for a PIN
 * session for exactly this). Not bound for a guest session or a locked screen: neither may reach the route,
 * and a guest is an outsider on someone else's dial with no account preference to follow.
 */
export function ThemeAccountSync() {
  const { me, lock } = useAuth();
  const { bindAccount } = useTheme();

  const userId = me?.userId ?? null;
  const pinned = me?.pinnedToInstanceId ?? null;
  const guest = !!me?.guestInstanceId;
  const locked = !!lock;

  useEffect(() => {
    if (!userId || guest || locked) {
      bindAccount(null);
      return;
    }
    bindAccount({
      load: async () => {
        const token = getAccessToken();
        return token ? await api.myTheme(token) : null;
      },
      save: async (theme) => {
        const token = getAccessToken();
        if (token) await api.setMyTheme(token, theme);
      },
    });
    return () => bindAccount(null);
  }, [userId, pinned, guest, locked, bindAccount]);

  return null;
}
